import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { range } from 'lodash'
import { Box, Card, CardContent, Grid, Icon as IconMui, Popover } from '@mui/material'
import IAEauAction from '../../../../iaeau/IAEauAction'
import AppStore from '../../../../store/AppStore'
import WaitAction from '../../../../wait/WaitAction'
import DtoPredStat from '../../../../iaeau/dto/DtoPredStat'
import { HORIZON_MODE } from '../../../../iaeau/constants/IAEauConstants'
import { SmallStyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import Checkbox from '../../../../components/forms/Checkbox'
import Button from '../../../../components/forms/Button'
import Input from '../../../../components/forms/Input'
import Select from '../../../../components/forms/Select'
import Icon from '../../../../components/icon/Icon'
import { ButtonMUI } from '../../../../components/styled/Buttons'
import { STATION_TYPE_NAME } from '../../../../station/constants/StationConstants'
import { getMapSituationCalendar } from '../../../../station/components/mapSituation/MapSituationUtils'
import { getDate, getHour, getMonthList } from '../../../../utils/DateUtil'
import { disabledColor, mainBlue, mainWhite } from '../../../../utils/constants/ColorTheme'
import { MODELS_TAB } from '../constants/PluvioChartTypes'
import { CardTitle } from 'components/card/NewCard'

// Tab "Données prévisionnelles" du suivi pluvio — fork simplifié de HydroSuiviModelTab
// adapté à une station unique (pas d'associations cross-stations) et au cumul pluvio.
// Émet vers le parent `{ predMeasures, selectedPred, predStats }` consommés ensuite par
// buildModelSeries dans GraphGenericPluvio pour générer les séries ECharts.

const DATE_BUTTON_STYLE = {
    padding: 0,
    minWidth: 0,
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '5rem',
}

const years = range(moment().year(), 1949, -1).map(y => ({ value: y, label: y }))

const buildKey = (pluviometerId, pred) => `${pluviometerId}:${pred.typeId}:${pred.source}`

const PluvioSuiviModelsTab = ({
    tab,
    pluviometerId,
    typeId,
    cumulPluvio,
    changeParent = () => {},
}) => {
    const [predStats, setPredStats] = useState([])
    // Pas de persistance localStorage : à chaque session, aucun modèle n'est coché
    // par défaut. L'utilisateur choisit ce qu'il veut afficher manuellement.
    const [modelCheck, setModelCheck] = useState({})
    // Sélection réellement APPLIQUÉE (bouton Appliquer). Cocher/décocher ne touche que
    // modelCheck ; l'affichage des courbes ne change qu'au clic sur Appliquer.
    const [appliedModelCheck, setAppliedModelCheck] = useState({})
    const [modelDate, setModelDate] = useState({})
    const [prevSucc, setPrevSucc] = useState({})
    const [modelCalendarDates, setModelCalendarDates] = useState({})
    const [selectedMonth, setSelectedMonth] = useState({})
    const [selectedYear, setSelectedYear] = useState({})
    const [calendarOpen, setCalendarOpen] = useState({})
    const [calendarRefs, setCalendarRefs] = useState({})

    // Fetch des prédictions disponibles à chaque changement de station ou de typeId.
    useEffect(() => {
        if (!pluviometerId) return
        IAEauAction.promisePredStats(STATION_TYPE_NAME.pluviometry, parseInt(pluviometerId, 10)).then(json => {
            const station = {
                stationType: STATION_TYPE_NAME.pluviometry,
                stationId: parseInt(pluviometerId, 10),
                stationTypeId: typeId,
                codepoint: null,
                label: '',
                code: '',
                name: '',
            }
            const newPreds = json.map(p => new DtoPredStat(p, station))
            const initialFor = (selector) => newPreds.reduce((acc, p) => ({
                ...acc,
                [buildKey(pluviometerId, p)]: selector(p),
            }), {})
            setPredStats(newPreds)
            setModelDate(initialFor(p => p.maxSimulationDate))
            setModelCalendarDates(initialFor(p => p.dates))
            setSelectedMonth(initialFor(p => p.maxSimulationDate))
            setSelectedYear(initialFor(p => p.maxSimulationDate))
            setPrevSucc(initialFor(p => [p.previousDate, undefined]))
            setCalendarRefs(initialFor(() => React.createRef()))
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pluviometerId, typeId])

    const reloadModelResultsDates = (newSelectedMonth, newSelectedYear, pred) => {
        const key = buildKey(pluviometerId, pred)
        const targetDate = moment(newSelectedYear).month(moment(newSelectedMonth).month()).valueOf()
        IAEauAction.promiseModelResultDates(STATION_TYPE_NAME.pluviometry, pluviometerId, pred.idModel, pred.source, targetDate)
            .then(predResultDates => {
                setSelectedMonth(prev => ({ ...prev, [key]: newSelectedMonth }))
                setSelectedYear(prev => ({ ...prev, [key]: newSelectedYear }))
                setModelCalendarDates(prev => ({ ...prev, [key]: predResultDates }))
            })
    }

    const changePrevSucc = (pred, newDate) => {
        const key = buildKey(pluviometerId, pred)
        if (!newDate) return
        setModelDate(prev => ({ ...prev, [key]: newDate }))
        IAEauAction.promiseModelResultPrevSuccDates(STATION_TYPE_NAME.pluviometry, pluviometerId, pred.idModel, pred.source, newDate).then(res => {
            setPrevSucc(prev => ({ ...prev, [key]: res }))
        })
    }

    const loadModels = (currentModelCheck, currentPredStats, currentModelDate) => {
        const activeKeys = Object.keys(currentModelCheck).filter(k => currentModelCheck[k])
        const predsToLoad = activeKeys
            .map(key => {
                const [, stationTypeId, source] = key.split(':')
                return currentPredStats.find(p => p.typeId === parseInt(stationTypeId, 10) && p.source === source)
            })
            .filter(p => !!p)

        if (!predsToLoad.length) {
            changeParent({ predMeasures: {}, selectedPred: undefined, predStats: currentPredStats })
            return
        }

        AppStore.dispatch(WaitAction.waitStart())
        Promise.all(predsToLoad.map(pred => IAEauAction.promiseModelMeasures(
            STATION_TYPE_NAME.pluviometry,
            parseInt(pluviometerId, 10),
            pred.idModel,
            pred.source,
            currentModelDate[buildKey(pluviometerId, pred)],
            cumulPluvio,
            pred.typeId,
        ))).then(resultsTab => {
            AppStore.dispatch(WaitAction.waitStop())
            // Indexé par clé complète (pluvioId:typeId:source) et NON par typeId : plusieurs
            // modèles d'un même type de donnée peuvent être affichés en même temps.
            const newPredMeasures = predsToLoad.reduce((acc, pred, idx) => ({
                ...acc,
                [buildKey(pluviometerId, pred)]: resultsTab[idx],
            }), {})
            const newSelectedPred = predsToLoad.reduce((acc, pred) => ({
                ...acc,
                [buildKey(pluviometerId, pred)]: pred,
            }), {})
            changeParent({ predMeasures: newPredMeasures, selectedPred: newSelectedPred, predStats: currentPredStats })
        }).catch(() => AppStore.dispatch(WaitAction.waitStop()))
    }

    const onValidate = () => {
        setAppliedModelCheck(modelCheck)
        loadModels(modelCheck, predStats, modelDate)
    }

    // Les courbes ne s'affichent QUE sur clic « Appliquer » (onValidate) : cocher/décocher
    // ne modifie plus l'affichage. On recharge uniquement les modèles DÉJÀ appliqués quand
    // le cumul change, pour garder leur agrégation (SUM_X) cohérente avec le graphe.
    useEffect(() => {
        if (!cumulPluvio || !predStats.length) return
        loadModels(appliedModelCheck, predStats, modelDate)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cumulPluvio])

    if (tab !== MODELS_TAB) return null

    const visiblePreds = predStats.filter(p => p.typeId === typeId)

    return (
        <Card>
            <CardTitle title={i18n.prevData} />
            <CardContent>
                <Box sx={{ padding: '1rem' }}>
                    <Grid container rowSpacing={1.5} justifyContent='center'>
                        <Grid size={12}>
                            <Card elevation={10}>
                                <CardContent>
                                    {visiblePreds.map(pred => {
                                        const key = buildKey(pluviometerId, pred)
                                        const prevColor = prevSucc[key]?.[0] ? mainBlue : disabledColor
                                        const nextColor = prevSucc[key]?.[1] ? mainBlue : disabledColor
                                        const isHours = pred.horizonMode === HORIZON_MODE.HOURS
                                        return (
                                            <Grid size={12} key={key}>
                                                <SmallStyledFieldSet>
                                                    <StyledLegend>{`${pred.source} (${pred.horizon} ${i18n[pred.horizonMode || 'days']})`}</StyledLegend>
                                                    <Grid container alignItems='center' columnSpacing={2}>
                                                        <Grid>
                                                            <Checkbox
                                                                checked={!!modelCheck[key]}
                                                                onChange={v => setModelCheck({ ...modelCheck, [key]: v })}
                                                                componentClassName='no-margin'
                                                            />
                                                        </Grid>
                                                        <Grid container alignItems='center' size='grow' columnSpacing={1}>
                                                            <Grid>
                                                                <ButtonMUI
                                                                    variant='outlined'
                                                                    onClick={() => changePrevSucc(pred, prevSucc[key]?.[0])}
                                                                    sx={{
                                                                        border: `solid 2px ${prevColor}`,
                                                                        color: prevColor,
                                                                        ...DATE_BUTTON_STYLE,
                                                                        '&:hover': prevSucc[key]?.[0] && {
                                                                            backgroundColor: prevColor,
                                                                            color: mainWhite,
                                                                        },
                                                                    }}
                                                                >
                                                                    <IconMui sx={{ fontSize: 18, color: 'inherit' }}>keyboard_double_arrow_left</IconMui>
                                                                </ButtonMUI>
                                                            </Grid>
                                                            <Grid
                                                                size={{ xl: isHours ? 4.5 : 7.5, lg: 5 }}
                                                                ref={calendarRefs[key]}
                                                                onClick={() => setCalendarOpen({ ...calendarOpen, [key]: true })}
                                                            >
                                                                <Input
                                                                    title={i18n.date}
                                                                    value={getDate(modelDate[key])}
                                                                    onChange={() => setModelDate({ ...modelDate, [key]: modelDate[key] })}
                                                                />
                                                            </Grid>
                                                            {isHours && (
                                                                <Grid size={2}>
                                                                    <Input title={i18n.hour} value={getHour(modelDate[key])} disabled />
                                                                </Grid>
                                                            )}
                                                            <Grid>
                                                                <ButtonMUI
                                                                    variant='outlined'
                                                                    onClick={() => changePrevSucc(pred, prevSucc[key]?.[1])}
                                                                    sx={{
                                                                        border: `solid 2px ${nextColor}`,
                                                                        color: nextColor,
                                                                        ...DATE_BUTTON_STYLE,
                                                                        '&:hover': prevSucc[key]?.[1] && {
                                                                            backgroundColor: nextColor,
                                                                            color: mainWhite,
                                                                        },
                                                                    }}
                                                                >
                                                                    <IconMui sx={{ fontSize: 18, color: 'inherit' }}>keyboard_double_arrow_right</IconMui>
                                                                </ButtonMUI>
                                                            </Grid>
                                                        </Grid>
                                                        <Popover
                                                            open={!!calendarOpen[key]}
                                                            anchorEl={calendarRefs[key]?.current}
                                                            onClose={() => setCalendarOpen({ ...calendarOpen, [key]: false })}
                                                        >
                                                            <Grid container sx={{ width: 250, padding: '0.5rem' }}>
                                                                <Grid container size={12}>
                                                                    <Grid size={1.5}>
                                                                        <Icon
                                                                            style={{ fontSize: 27 }}
                                                                            clickable
                                                                            icon='chevron_left'
                                                                            onClick={() => {
                                                                                if (moment(selectedMonth[key]).month() + 1 === 1) {
                                                                                    reloadModelResultsDates(moment().month(11).valueOf(), moment(selectedYear[key]).subtract(1, 'year').valueOf(), pred)
                                                                                } else {
                                                                                    reloadModelResultsDates(moment(selectedMonth[key]).subtract(1, 'month').valueOf(), selectedYear[key], pred)
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid size={4.5}>
                                                                        <Select
                                                                            value={moment(selectedMonth[key]).month() + 1}
                                                                            options={getMonthList()}
                                                                            onChange={v => reloadModelResultsDates(moment().month(v - 1).valueOf(), selectedYear[key], pred)}
                                                                            noInputFieldClass
                                                                            noSort
                                                                        />
                                                                    </Grid>
                                                                    <Grid size={1.5}>
                                                                        <Icon
                                                                            style={{ fontSize: 27 }}
                                                                            clickable
                                                                            icon='chevron_right'
                                                                            onClick={() => {
                                                                                if (moment(selectedMonth[key]).month() + 1 === 12) {
                                                                                    reloadModelResultsDates(moment().month(0).valueOf(), moment(selectedYear[key]).add(1, 'year').valueOf(), pred)
                                                                                } else {
                                                                                    reloadModelResultsDates(moment(selectedMonth[key]).add(1, 'month').valueOf(), selectedYear[key], pred)
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid size={4.5}>
                                                                        <Select
                                                                            value={moment(selectedYear[key]).year()}
                                                                            options={years}
                                                                            onChange={v => reloadModelResultsDates(selectedMonth[key], moment().year(v).valueOf(), pred)}
                                                                            noInputFieldClass
                                                                            noSort
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid size={12}>
                                                                    {getMapSituationCalendar(
                                                                        selectedYear[key],
                                                                        selectedMonth[key],
                                                                        modelCalendarDates[key] || [],
                                                                        modelDate[key],
                                                                        v => setModelDate({ ...modelDate, [key]: v }),
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Popover>
                                                    </Grid>
                                                </SmallStyledFieldSet>
                                            </Grid>
                                        )
                                    })}
                                    {!visiblePreds.length && (
                                        <Grid size={12}>
                                            <h6>{i18n.noModelAvailable}</h6>
                                        </Grid>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid>
                            <Button
                                tooltip={i18n.apply}
                                onClick={onValidate}
                                icon='border_color'
                                className='btn-floating btn-large'
                                disabled={!visiblePreds.length}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </CardContent>
        </Card>
    )
}

PluvioSuiviModelsTab.propTypes = {
    tab: PropTypes.string,
    pluviometerId: PropTypes.number,
    typeId: PropTypes.number,
    cumulPluvio: PropTypes.string,
    changeParent: PropTypes.func,
}

export default PluvioSuiviModelsTab
